/** 설정: 제출 알림 메일 · 권역별 Missionary 서명 */
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { api, fileUrl } from '../api';
import { C } from '../common';
import { Card } from './ui';

type Settings = { notifyEmailEnabled: boolean; notifyEmail: string; notifyAttachPhotos: boolean; regionalSignatures: Record<string, string>; regions: string[]; mailConfigured: boolean };

const SettingsTab: React.FC = () => {
  const [s, setS] = useState<Settings | null>(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [ver, setVer] = useState(Date.now());
  const load = () => api<{ data: Settings }>('/admin/cert/settings').then((r) => setS(r.data)).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);
  if (!s) return error ? <Alert severity="error">{error}</Alert> : null;

  const save = async () => {
    setError(''); setMsg('');
    try { await api('/admin/cert/settings', { method: 'PUT', body: s }); setMsg('저장했습니다.'); } catch (e: any) { setError(e.message); }
  };
  const test = async () => {
    try { const r = await api<{ message: string }>('/admin/cert/settings/test-mail', { body: {} }); setMsg(r.message); } catch (e: any) { setError(e.message); }
  };
  const upload = async (region: string, file?: File) => {
    if (!file) return;
    const form = new FormData();
    form.append('signature', file);
    try { await api(`/admin/cert/signatures/${encodeURIComponent(region)}`, { form }); setVer(Date.now()); load(); } catch (e: any) { setError(e.message); }
  };
  const removeSig = async (region: string) => { await api(`/admin/cert/signatures/${encodeURIComponent(region)}`, { method: 'DELETE' }); load(); };

  return (
    <Box sx={{ display: 'grid', gap: 2, maxWidth: 880 }}>
      {msg && <Alert severity="success" onClose={() => setMsg('')}>{msg}</Alert>}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      <Card title="완성보고서 제출 알림 메일">
        {!s.mailConfigured && <Alert severity="warning" sx={{ mb: 1.5 }}>서버에 SMTP 설정(SMTP_HOST 등)이 없어 메일이 발송되지 않습니다. 관리자 화면의 🔔 알림·배지는 그대로 동작합니다.</Alert>}
        <FormControlLabel control={<Switch checked={s.notifyEmailEnabled} onChange={(e) => setS({ ...s, notifyEmailEnabled: e.target.checked })} />} label="교회가 보고서를 올리면 메일 보내기" />
        <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="받는 주소" value={s.notifyEmail} onChange={(e) => setS({ ...s, notifyEmail: e.target.value })} sx={{ width: 280 }} />
          <FormControlLabel control={<Checkbox checked={s.notifyAttachPhotos} onChange={(e) => setS({ ...s, notifyAttachPhotos: e.target.checked })} />} label="보고서 사진 첨부" />
          <Button variant="contained" onClick={save}>저장</Button>
          <Button onClick={test}>테스트 메일</Button>
        </Box>
        <Typography fontSize={12.5} color={C.sub} mt={1}>제목 예: [BT 완성보고서] 새빛교회(0101) 3장 제출 — 본문에 교회·담당자·날짜요청·교사/클럽 표와 ‘승인 화면에서 확인하기’ 버튼이 들어갑니다.</Typography>
      </Card>
      <Card title="Missionary 서명 (권역별)">
        <Typography fontSize={13} color={C.sub} mb={1.5}>지금은 모든 수료증에 <b>김승정</b> 서명이 들어갑니다. 권역 서명을 올리면 그 권역 교회(교회 주소 기준) 수료증에 자동으로 바뀌어 들어갑니다. 배경이 투명한 PNG를 올려주세요.</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
          <SigBox title="기본 (전체)" src="/bt-cert/sig-default.png" note="김승정" />
          {s.regions.map((r) => (
            <SigBox key={r} title={r} src={s.regionalSignatures[r] ? `${fileUrl(`/cert/signature/${encodeURIComponent(r)}`)}?v=${ver}` : ''} note={s.regionalSignatures[r] ? '권역 서명 사용' : '기본 서명 사용'}>
              <Button component="label" size="small">올리기<input hidden type="file" accept="image/png" onChange={(e) => upload(r, e.target.files?.[0])} /></Button>
              {s.regionalSignatures[r] && <Button size="small" color="error" onClick={() => removeSig(r)}>지우기</Button>}
            </SigBox>
          ))}
        </Box>
      </Card>
    </Box>
  );
};

const SigBox: React.FC<{ title: string; src: string; note: string; children?: React.ReactNode }> = ({ title, src, note, children }) => (
  <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 2, p: 1.25, bgcolor: '#fff' }}>
    <Typography fontWeight={800} fontSize={14}>{title}</Typography>
    <Box sx={{ height: 70, display: 'grid', placeItems: 'center', my: 0.5, bgcolor: '#fafafa', borderRadius: 1 }}>
      {src ? <img src={src} alt="" style={{ maxHeight: 64, maxWidth: '100%' }} /> : <Typography fontSize={12} color={C.sub}>—</Typography>}
    </Box>
    <Typography fontSize={12} color={C.sub}>{note}</Typography>
    {children}
  </Box>
);

export default SettingsTab;
